import { oc } from "@orpc/contract";
import { z } from "zod";

export const SidebarNavSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  itemIds: z.array(z.string()),
});

export const SidebarNavLayoutSchema = z.object({
  sections: z.array(SidebarNavSectionSchema),
  // Items hidden from the sidebar but kept in their section slot (so toggling
  // visibility preserves their position). A subset of the section itemIds.
  hiddenItemIds: z.array(z.string()).default([]),
  // Items removed from all sections into the "Unused" bucket. Not rendered in
  // the sidebar and not re-added automatically.
  unusedItemIds: z.array(z.string()).default([]),
  // Sections hidden from the sidebar (all their items excluded) but kept in
  // place in the editor. A subset of section ids.
  hiddenSectionIds: z.array(z.string()).default([]),
});

export const SidebarPreferencesSchema = z.object({
  collapsedGroups: z.array(z.string()).default([]),
  hiddenItems: z.array(z.string()).default([]),
  layout: SidebarNavLayoutSchema.optional(),
});

// Remembers the buyer's last-used division selection on the procurement
// replenishment page so it survives across sessions (the URL stays the
// shareable source of truth; this only seeds the default when none is set).
export const ProcurementDivisionFilterSchema = z.object({
  divisionIds: z.array(z.string()).default([]),
});

// Remembers the buyer's last-used destination-warehouse selection on the
// replenishment page (scoped to the selected divisions). Same role as the
// division filter above — seeds the default when the URL carries none.
export const ProcurementWarehouseFilterSchema = z.object({
  warehouseIds: z.array(z.string()).default([]),
});

// Remembers the buyer's last-used buy-vs-transfer view on the replenishment
// page: 'all' (supplier carts + transfers), 'po' (purchases only), 'to'
// (transfers only).
export const ProcurementViewModeSchema = z.object({
  view: z.enum(["all", "po", "to"]).default("all"),
});

// Per-user delivery choice for the notification system. In-app is always on
// (the bell feed is the baseline); "both" opts in to email on top. Defaults
// to in-app only. NotificationService intersects an event's channels with
// this preference per recipient; when the intersection is empty it falls
// back to in-app so a notification is never silently dropped.
export const NotificationDeliveryChannelSchema = z.enum(["in_app", "both"]);

export const NotificationDeliveryPreferenceSchema = z.object({
  channel: NotificationDeliveryChannelSchema.default("in_app"),
});

// Per-user email signature appended to outgoing emails (AR statements first;
// reusable by other flows). The HTML is rich text that may embed signature
// images as `<img data-document-id="...">`. The document id is the source of
// truth for an image; the `src` attribute is regenerated per context (a fresh
// signed URL for in-app preview, a `cid:` reference at send time). `enabled`
// lets a user keep a saved signature but stop appending it without deleting it.
export const EmailSignatureSchema = z.object({
  html: z.string().default(""),
  enabled: z.boolean().default(true),
});

const preferenceValueSchemas = {
  sidebar: SidebarPreferencesSchema,
  procurementDivisionFilter: ProcurementDivisionFilterSchema,
  procurementWarehouseFilter: ProcurementWarehouseFilterSchema,
  procurementViewMode: ProcurementViewModeSchema,
  notificationDelivery: NotificationDeliveryPreferenceSchema,
  emailSignature: EmailSignatureSchema,
} as const;

export type PreferenceKey = keyof typeof preferenceValueSchemas;

export const PreferenceKeySchema = z.enum(
  Object.keys(preferenceValueSchemas) as [PreferenceKey, ...PreferenceKey[]],
);

export const PreferenceValueSchema = z.record(z.string(), z.unknown());

export function getValueSchemaForKey(key: PreferenceKey) {
  return preferenceValueSchemas[key];
}

export const GetUserPreferenceInputSchema = z.object({
  key: PreferenceKeySchema,
});

export const GetUserPreferenceResponseSchema = z
  .object({
    key: z.string(),
    value: PreferenceValueSchema,
  })
  .nullable();

export const UpsertUserPreferenceInputSchema = z.object({
  key: PreferenceKeySchema,
  value: PreferenceValueSchema,
});

export const UpsertUserPreferenceResponseSchema = z.object({
  key: z.string(),
  value: PreferenceValueSchema,
});

export const userPreferencesContracts = {
  get: oc
    .input(GetUserPreferenceInputSchema)
    .output(GetUserPreferenceResponseSchema)
    .route({ method: "GET" }),
  upsert: oc
    .input(UpsertUserPreferenceInputSchema)
    .output(UpsertUserPreferenceResponseSchema)
    .route({ method: "POST" }),
} as const;

export type GetUserPreferenceInput = z.infer<
  typeof GetUserPreferenceInputSchema
>;
export type GetUserPreferenceResponse = z.infer<
  typeof GetUserPreferenceResponseSchema
>;
export type UpsertUserPreferenceInput = z.infer<
  typeof UpsertUserPreferenceInputSchema
>;
export type UpsertUserPreferenceResponse = z.infer<
  typeof UpsertUserPreferenceResponseSchema
>;
export type EmailSignature = z.infer<typeof EmailSignatureSchema>;
export type SidebarPreferences = z.infer<typeof SidebarPreferencesSchema>;
export type NotificationDeliveryChannel = z.infer<
  typeof NotificationDeliveryChannelSchema
>;
export type NotificationDeliveryPreference = z.infer<
  typeof NotificationDeliveryPreferenceSchema
>;
export type ProcurementDivisionFilter = z.infer<
  typeof ProcurementDivisionFilterSchema
>;
export type SidebarNavLayout = z.infer<typeof SidebarNavLayoutSchema>;
export type SidebarNavSection = z.infer<typeof SidebarNavSectionSchema>;
